/**
 * How an instant is written on screen.
 *
 * ## Why not the local timezone
 *
 * Every timestamp in this product is UTC: the audit trail is partitioned by UTC
 * day, `MAX_AUDIT_DAYS` iterates UTC dates, a run's `requestedAt` is what the
 * customer's own CloudTrail will show, and the people reading these are in
 * several timezones talking to each other about the same incident. Rendering a
 * viewer's local time would make two engineers on one call read different
 * numbers off the same row — and the day boundary they filter on would stop
 * matching the day boundary the store uses.
 *
 * So the zone is UTC and it is *said*, rather than left for someone to assume.
 *
 * ## Why the ISO string is still in the DOM
 *
 * These render inside `<time dateTime={iso}>`. The exact value is what gets
 * pasted into a query or a ticket, so it stays machine-readable even though it
 * is not what is displayed.
 */

/** `2026-09-09 11:04 UTC`, or with seconds where they distinguish two rows. */
export const formatInstant = (iso: string, seconds = false): string => {
  const at = new Date(iso)
  // Not every string reaching here came from the API — an unparseable one is
  // shown as it arrived rather than as "Invalid Date", which tells nobody
  // anything and hides the value that would explain it.
  if (Number.isNaN(at.getTime())) return iso

  const pad = (n: number): string => String(n).padStart(2, '0')
  const date = `${String(at.getUTCFullYear())}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}`
  const time = `${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}${
    seconds ? `:${pad(at.getUTCSeconds())}` : ''
  }`
  return `${date} ${time} UTC`
}

/**
 * How long ago an instant was, in the coarsest unit that still says something.
 *
 * For "how long has this been live" (IM-129), where the useful reading is
 * *four months* rather than a date somebody has to subtract from today. The
 * exact instant is still in the `dateTime` attribute and in the title, so the
 * precise answer is one hover or one paste away.
 *
 * Coarse on purpose. Days past a couple of months is a number nobody compares
 * — "128 days" and "134 days" mean the same thing to a reader — and months are
 * what the question is actually asked in.
 *
 * **No verdict.** This says how old, never whether that is too old. Every real
 * customer's production instance is long-lived, and only its owner knows
 * whether a given one is a forgotten demo or the thing a business runs on —
 * `shared/src/discovery/instances.ts` states the rule and it holds here
 * unchanged. A screen that flags the permanent ones as waste is one people stop
 * reading, and what they stop reading it about will eventually be the thing
 * that mattered.
 */
export const formatAge = (iso: string, now: Date = new Date()): string => {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso

  const days = Math.floor((now.getTime() - at.getTime()) / 86_400_000)
  // A clock skew or a record from the future reads as "today" rather than as a
  // negative number, which would look like a fault in the page.
  if (days <= 0) return 'today'
  if (days === 1) return '1 day'
  if (days < 60) return `${String(days)} days`
  const months = Math.floor(days / 30)
  if (months < 24) return `${String(months)} months`
  return `${String(Math.floor(days / 365))} years`
}

/**
 * Money, in the currency AWS reported it in.
 *
 * `Intl.NumberFormat` rather than a hand-rolled `toFixed`, because the symbol
 * and its placement belong to the currency and not to this file — and because a
 * figure this is used for gets compared against an AWS invoice, where being off
 * by a rounding rule is worse than being absent.
 *
 * The locale is fixed to `en-US` for the same reason `formatInstant` fixes the
 * zone to UTC: two engineers reading the same row on a call must see the same
 * number, and a thousands separator that moves with the browser is how they
 * stop.
 */
export const formatMoney = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    // An unrecognised currency code throws rather than falling back, and a
    // thrown formatter would blank a whole panel over a label.
    return `${amount.toFixed(2)} ${currency}`
  }
}
